import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import type { LoaderFunction, MetaFunction } from '@remix-run/node';
import { json } from '@remix-run/node';
import { Link, useLoaderData, useNavigate } from '@remix-run/react';

import Breadcrumbs from '~/components/Breadcrumbs';
import { Image } from '~/components/Image';
import Text from '~/components/Text';
import remixI18n from '~/i18next.server';
import type { Author } from '~/interfaces/author';
import AuthorIcons from '~/pages/authors/AuthorIcons';
import getAuthors from '~/services/api/authors/getAuthors.server';
import getPublication from '~/services/api/publication/getPublication.server';
import { loaderSession } from '~/session';
import getCanonicalUrl from '~/utils/getCanonicalUrl';
import getMergedMetaData from '~/utils/getMergedMetaData';

export const meta: MetaFunction<typeof loader> = ({ data, matches }) => {
  const publication = data?.publication;
  const metaTitle = data?.metaTitle;
  const metaDescription = data?.metaDescription;

  return getMergedMetaData({
    canonicalUrl: getCanonicalUrl(data?.requestUrl),
    matches,
    returnEarlyCheck: !metaTitle,
    overrides: {
      title: metaTitle,
      description: metaDescription,
    },
    structuredData: {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          item: {
            '@id': publication?.url,
            name: publication?.name,
          },
        },
      ],
    },
  });
};

export const loader: LoaderFunction = async ({ request }: any) => {
  const { session } = await loaderSession(request);

  const url = new URL(request.url);

  const [authors, publication] = await Promise.all([getAuthors(request, session), getPublication(request, session)]);
  const t = await remixI18n.getFixedT(publication?.language || 'en', 'common');

  return json({
    publication,
    requestUrl: url.href,
    metaTitle: t('author.authors'),
    metaDescription: t('author.description'),
    authors: authors,
  });
};

const Authors = () => {
  const { publication, authors } = useLoaderData<typeof loader>();
  const { t } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    if (authors.authors.length === 0) {
      navigate('/');
    }
  }, [authors, navigate]);

  const breadCrumbs = [
    {
      text: publication?.name,
      href: '../',
    },
    {
      text: 'Authors',
      href: './',
    },
  ];

  return (
    <div className="px-4">
      {publication.render_breadcrumbs && (
        <div className="mx-auto mt-8 max-w-6xl py-4">
          <Breadcrumbs breadcrumbs={breadCrumbs} />
        </div>
      )}
      <div className="mx-auto flex w-full max-w-6xl pb-16 pt-4">
        <div className="flex w-full flex-col space-y-8">
          <div className="w-full space-y-2">
            <Text as="h1" size="4xl" type="title" theme="default" className="font-semibold text-wt-text-on-background">
              {t('author.authors')}
            </Text>
            <Text as="p" size="xl" type="body" theme="default" className="text-wt-text-on-background">
              {t('author.description')} {publication.name}
            </Text>
          </div>

          <div className="grid w-full grid-cols-1 gap-8 sm:grid-cols-2 md:grid-cols-3">
            {authors.authors.map((author: Author) => {
              return (
                <div key={author.id} className="relative mb-4">
                  <Link to={author.id} className="relative" prefetch="intent">
                    <div className="z-10 col-span-1 mb-2 w-full overflow-hidden rounded-wt">
                      <Image
                        src={author.profile_picture.url}
                        alt={author.name}
                        maxWidth={800}
                        loading="lazy"
                        aspectRatio="social"
                      />
                    </div>

                    <div className="absolute inset-0 z-0 -m-4 rounded-wt bg-transparent transition duration-200 hover:bg-slate-100/10 sm:-m-6" />

                    <div className="space-y-2">
                      <Text
                        as="h4"
                        size="xl"
                        type="title"
                        theme="default"
                        className="font-semibold text-wt-text-on-background"
                      >
                        {author.name}
                      </Text>
                      {author.bio && (
                        <div className="pb-2">
                          <Text as="p" size="sm" type="body" theme="default" className="text-wt-text-on-background">
                            {author.bio}
                          </Text>
                        </div>
                      )}
                    </div>
                  </Link>
                  <AuthorIcons author={author} className="absolute flex space-x-2 pt-1" />
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Authors;
